import { Grid, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Column, Spacer, StepsHeader } from '../../components';
import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { SignUpWizard as SignUpWizardService, signUpWizardKey } from '../../services/signUp';
import { ToastbarContext } from '../../App';
import { OrgSignUpStateData } from '../../models/SignUpData';
import { Toast } from '../../models/Toast';
import { PricebookBundle, PaymentTypeEnum } from '../../models/PricebookData';
import { AcceptLicenseAgreement } from '../../data/license/mutations';
import { UpdateSignUpState } from '../../data/signUp/mutations';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { LicenseCompartments, licenseStorageKey } from '../../data/license';
import { API_URL } from '../../api/apis';
import { LicenseAgreementData } from '../../api/apis/LicenseAgreementApi';
import { useDeviceType } from '../../hooks/useDeviceType';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { AdminPaymentApi } from '../../api/apis/AdminPaymentApi';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { OrganizationData } from '../../models/OrganizationData';
import { useLoading } from '../../hooks/useLoading';
import LicenseModal from './LicenseModal';
import { ViewContractButton } from './ViewContractButton';
import { ContractActionButtons } from './ContractActionButtons';
import Cookies from 'js-cookie';
import { LicenseAgreementAcceptLogData } from '../../api/apis/LicenseAgreementAcceptLogApi';
import {
  licenseAgreementAcceptLogCompartments,
  licenseAgreementAcceptLogStorageKey,
} from '../../data/licenseAgreementAcceptLog';
import ContractFooter from './ContractFooter';
import { PaymentCompartments, paymentStorageKey } from '../../data/payment';
import { AdminCompartments, adminStorageKey } from '../../data/admin';
import { AdminStatusEnum, UserData } from '../../models/UserData';
import { UserCompartments, userStorageKey } from '../../data/user';
import ContractPaymentDetails from './ContractPaymentDetails';
import { UserApi } from '../../api/apis/UserApi';
import { StepHeaderType } from '../../models/StepsHeaderData';
import useTermsOfServiceDocs from '../../hooks/useTermsOfServiceDocs';
import { PaidByEnum } from '../../models/LicenseData';
import { usePricebookSelections } from '../../hooks/usePricebookOptions';
import { isFreemiumPricebook } from '../../helpers/pricebookUtils';

const Contract = () => {
  const appStorage = useAppStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const termsOfServiceDocs = useTermsOfServiceDocs();

  const updateSignUpState = useMutation(new UpdateSignUpState());
  const acceptLicenseAgreement = useMutation(new AcceptLicenseAgreement());
  const signUpWizard = useService<SignUpWizardService>(signUpWizardKey);

  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const licenseAgreementAcceptLogDataCache =
    appStorage.retrieve<licenseAgreementAcceptLogCompartments>(licenseAgreementAcceptLogStorageKey);
  const paymentDataCache = appStorage.retrieve<PaymentCompartments>(paymentStorageKey);
  const adminDataCache = appStorage.retrieve<AdminCompartments>(adminStorageKey);
  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);

  const adminPaymentApi = useService<AdminPaymentApi>(ApiKeys.AdminPayment);
  const userApi = useService<UserApi>(ApiKeys.User);

  const pricebookSelections = usePricebookSelections();
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const organizationData = useObservable(
    organizationDataCache.observe$<OrganizationData>('organization'),
  );
  const paymentMethods = useObservable(
    paymentDataCache.observe$<PaymentMethodData[]>('paymentMethods'),
  );
  const orgAdmins = useObservable(adminDataCache.observe$<UserData[]>('orgAdmins')) ?? [];
  const userData = useObservable(userDataCache.observe$<UserData>('user'));
  const multipleProductTypesAvailable = useObservable(signUpWizard.multipleProductTypes$);

  const primaryBillingUser = orgAdmins.filter(
    admin => admin.status === AdminStatusEnum.Active && admin.primaryBillingUser,
  );

  const licenseData = useMemo(
    () => orgSignUpState?.state?.licenseData && JSON.parse(orgSignUpState?.state.licenseData),
    [orgSignUpState?.state?.licenseData],
  );

  const resumeAddPackage = location?.state?.redirectReason === 'abandonedAddPackage';
  const pendingAgreement =
    licenseAgreements &&
    licenseAgreements?.find(
      licenseAgreement => licenseAgreement.docStatus && !licenseAgreement.hasAccepted,
    );

  const pricebook: PricebookBundle = useMemo(() => {
    if (pendingAgreement && resumeAddPackage && !orgSignUpState?.state?.pricebook) {
      return pendingAgreement.pricebookBundleDto;
    }
    return orgSignUpState?.state?.pricebook && JSON.parse(orgSignUpState?.state.pricebook);
  }, [orgSignUpState?.state?.pricebook, pendingAgreement, resumeAddPackage]);

  const noCost = pricebook?.prices.every(price => price.price === 0);
  const isFreemium = pricebook?.pricebook ? isFreemiumPricebook(pricebook.pricebook) : false;
  const prePaid = pricebook?.pricebook?.paymentType === PaymentTypeEnum.Prepaid;
  const licenseAgreementAcceptLog =
    useObservable(
      licenseAgreementAcceptLogDataCache.observe$<Map<number, LicenseAgreementAcceptLogData>>(
        'licenseAgreementAcceptLog',
      ),
    ) ?? new Map<number, LicenseAgreementAcceptLogData>();

  const { activeStep, totalSteps } = signUpWizard.currentStep(location);
  const [open, setOpen] = useState(false);
  const [docUri, setDocUri] = useState<string | undefined>(undefined);
  const [doc, setDoc] = useState<LicenseAgreementData | undefined>(undefined);
  const [checkedOut, setCheckedOut] = useState<boolean>();
  const { isMobile, isTablet } = useDeviceType();
  const steps: StepHeaderType = orgSignUpState?.onboardingComplete
    ? 'addPackage'
    : multipleProductTypesAvailable
      ? 'onboardingMultipleProducts'
      : 'onboarding';

  const currentLicense = useMemo(() => {
    if (!licenseAgreements) return undefined;

    return licenseAgreements.find(
      license => license.pricebookId === pricebook?.pricebook?.id && license.docStatus,
    );
  }, [licenseAgreements, pricebook?.pricebook?.id]);

  const meteredPricebooks = pricebookSelections?.filter(
    pricebook => pricebook.pricebook?.paymentType === PaymentTypeEnum.Metered,
  );
  const meteredPayment = (meteredPricebooks?.length ?? 0) > 0;

  const isADP = pricebookSelections?.some(pricebook =>
    pricebook.prices.some(price => price.processor.name === 'ADP'),
  );
  const orgPays = useMemo(
    () =>
      currentLicense
        ? currentLicense?.pricebookBundleDto.pricebook.paidBy === PaidByEnum.Org
        : false,
    [currentLicense],
  );
  const reviewed = currentLicense?.hasAccepted && currentLicense?.docStatus;

  const agreeText = noCost
    ? 'Done'
    : orgPays &&
        meteredPayment &&
        organizationData?.automaticPayment &&
        (paymentMethods?.length ?? 0) === 0
      ? 'Add payment method'
      : orgPays && organizationData?.automaticPayment && !meteredPayment && !isADP
        ? 'Check out'
        : 'Done';

  useEffect(() => {
    const getDocUri = async () => {
      const doc = resumeAddPackage && pendingAgreement ? pendingAgreement : currentLicense;
      if (doc) {
        setDocUri(API_URL + 'license-agreement/doc?docId=' + doc.id);
        setDoc(doc);
      }
    };

    getDocUri();
  }, [currentLicense, pendingAgreement, resumeAddPackage]);

  useEffect(() => {
    if (!licenseAgreements) return;
    const hasContractToReview = licenseAgreements.some(agreement => agreement.docStatus);
    if (hasContractToReview) return;

    if (orgSignUpState?.onboardingComplete) {
      navigate('/addPackage', { replace: true });
    } else {
      navigate(signUpWizard.previousStep(location)?.route ?? '/onboarding/license', {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseAgreements, orgSignUpState?.onboardingComplete]);

  useEffect(() => {
    updateDateAccepted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLicense?.hasAccepted, doc, licenseAgreementAcceptLog.get(doc?.id ?? 0)]);

  useEffect(() => {
    if (open && !docUri) {
      setToast(
        new Toast({
          message: 'Error loading license agreement',
          severity: 'error',
          open: true,
          autoHideDuration: 3000,
        }),
      );
      setOpen(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docUri, open]);

  useEffect(() => {
    setCheckedOut(
      orgPays &&
        ((meteredPayment &&
          (paymentMethods?.length ?? 0) === 0 &&
          organizationData?.automaticPayment) ||
          !meteredPayment)
        ? false
        : undefined,
    );
  }, [orgPays, meteredPayment, paymentMethods?.length, organizationData?.automaticPayment]);

  useEffect(() => {
    if (acceptLicenseAgreement.error) {
      setLoading(false);
      setToast(
        new Toast({
          message: 'Error accepting license agreement',
          severity: 'error',
          open: true,
        }),
      );
    } else if (acceptLicenseAgreement.status === MutationStatus.Complete) {
      const dateAccepted = licenseAgreementAcceptLog.get(doc?.id ?? 0)?.dateAccepted;
      if (dateAccepted) {
        updateSignUpState
          .action({
            state: {
              ...orgSignUpState?.state,
              checkedOut: JSON.stringify(checkedOut),
              dateAccepted,
            },
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptLicenseAgreement.status, acceptLicenseAgreement.error]);

  const navigateToContractCompletion = () => {
    if (orgSignUpState?.onboardingComplete) {
      navigate('/addPackage/congrats', {
        state: { quantity: licenseData?.quantity, chosenPackage: pricebook },
      });
    } else {
      navigate(signUpWizard.nextStep(location).route);
    }
  };

  const navToNextStep = async () => {
    try {
      if (orgPays && primaryBillingUser.length === 0 && userData?.id) {
        await userApi.changePrimaryBillingUser(userData.id);
      }
      if (
        !isFreemium &&
        orgPays &&
        (organizationData?.automaticPayment || (prePaid && !orgSignUpState?.onboardingComplete))
      ) {
        const { data: stripeSession } = await adminPaymentApi.createSession(
          pricebook?.pricebook?.id ?? 0,
          licenseData?.quantity ?? 0,
        );
        await updateSignUpState.action({
          state: {
            ...orgSignUpState?.state,
            stripeSession: JSON.stringify(stripeSession),
            licenseData: JSON.stringify(licenseData),
            checkedOut: JSON.stringify(checkedOut),
          },
        });
        organizationDataCache.reload('allMetrics');

        if (stripeSession?.url) {
          window.location.replace(stripeSession.url);
          return;
        }
        navigateToContractCompletion();
      } else if (orgSignUpState?.onboardingComplete) {
        await updateSignUpState.action({
          state: {
            ...orgSignUpState?.state,
            pricebook: JSON.stringify(pricebook),
          },
        });
        navigateToContractCompletion();
      } else {
        navigateToContractCompletion();
      }
      setToast(
        new Toast({
          message: 'Contract successfully set up!',
          severity: 'success',
          open: true,
          autoHideDuration: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const navBack = async () => {
    try {
      setLoading(true);
      if (orgSignUpState?.onboardingComplete) {
        if (resumeAddPackage) {
          navigate('/');
        } else {
          const stripeCheckoutSession =
            orgSignUpState.state?.stripeSession && JSON.parse(orgSignUpState.state.stripeSession);
          if (!stripeCheckoutSession?.id) {
            navigate(-1);
          } else {
            navigate('/addPackage');
          }
        }
      } else {
        const previousStep = signUpWizard.previousStep(location);
        navigate(previousStep?.route ?? '', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await updateSignUpState.action({
        lastCompletedStep: !orgSignUpState?.onboardingComplete
          ? signUpWizard.getCurrent({
              ...location,
              pathname: location.pathname.replace('addPackage', 'onboarding'),
            })
          : orgSignUpState.lastCompletedStep,
      });

      await navToNextStep();

      if (currentLicense?.hasAccepted && doc && licenseAgreementAcceptLog.get(doc.id)) {
        await updateDateAccepted();
      }
    } catch (error) {
      console.error('Error on submit: ', error);
      setToast(
        new Toast({
          message: 'Error submitting contract',
          severity: 'error',
          open: true,
        }),
      );
    }
  };

  const onViewAgreement = () => {
    setOpen(true);
  };

  const onDownload = async () => {
    if (docUri) {
      const response = await fetch(docUri, {
        method: 'GET',
        headers: {
          'XSRF-TOKEN': Cookies.get('XSRF-TOKEN') ?? '',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        return setToast(
          new Toast({
            message: 'Error downloading file',
            severity: 'error',
            open: true,
          }),
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${organizationData?.name} License Agreement.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const onAgree = async () => {
    try {
      setLoading(true);
      if (doc) {
        setOpen(false);
        if (!licenseAgreementAcceptLog.get(doc.id)) {
          await acceptLicenseAgreement.action(doc.id);
        }
      }
    } catch (error) {
      console.error(error);
      setToast(
        new Toast({
          message: 'Error updating contract acceptance',
          severity: 'error',
          open: true,
        }),
      );
      setLoading(false);
    }
  };

  const updateDateAccepted = async () => {
    try {
      if (currentLicense?.hasAccepted && doc && licenseAgreementAcceptLog.get(doc.id)) {
        const { dateAccepted } = licenseAgreementAcceptLog.get(doc.id) ?? {};
        if (dateAccepted) {
          await updateSignUpState
            .action({
              state: {
                ...orgSignUpState?.state,
                checkedOut: JSON.stringify(checkedOut),
                dateAccepted: dateAccepted,
              },
            })
            .then(() => setLoading(false));
        }
      }
    } catch (error) {
      console.error('Error updating date accepted: ', error);
      setToast(
        new Toast({
          message: 'Error updating date',
          severity: 'error',
          open: true,
        }),
      );
    }
  };

  return open ? (
    <LicenseModal
      open={open}
      setOpen={setOpen}
      docUri={docUri}
      reviewed={reviewed}
      onDownload={onDownload}
      onAgree={onAgree}
    />
  ) : (
    <Column style={{ margin: isMobile || isTablet ? '15px 20px' : '15px 100px' }}>
      {!resumeAddPackage ? (
        <>
          <StepsHeader
            activeStep={orgSignUpState?.onboardingComplete ? 2 : activeStep}
            totalSteps={orgSignUpState?.onboardingComplete ? 2 : totalSteps}
            headerSteps={steps}
          />
          <Spacer height='xs' />
          <Typography variant='h1' color='secondary'>
            Step {orgSignUpState?.onboardingComplete ? 2 : activeStep}: Contract
          </Typography>
        </>
      ) : (
        <>
          <Typography variant='h1' color='secondary'>
            Contract
          </Typography>
          <Spacer height='xs' />
          <Typography variant='body1' color='error.dark'>
            Please review your outstanding license agreement for {organizationData?.name}.
          </Typography>
        </>
      )}
      <Spacer height='sm' />

      <Grid container width='70%' alignSelf='center'>
        <ContractPaymentDetails
          selectedPricebook={pricebook}
          licenseQuantity={licenseData?.quantity?.toString() ?? ''}
          costPerLicense={licenseData?.cost}
          invoiceDate={licenseData?.invoiceDate}
          orgPays={orgPays}
          meteredPayment={meteredPayment}
          autoPay={organizationData?.automaticPayment}
          prePaid={prePaid}
          noCost={noCost}
        />
      </Grid>
      <Spacer height='xs' />

      <ContractFooter
        meteredPayment={meteredPayment}
        autoPay={organizationData?.automaticPayment}
        orgPays={orgPays}
        prePaid={prePaid}
        invoiceDate={licenseData?.invoiceDate}
        tosLink={termsOfServiceDocs}
        noCost={noCost}
      />
      <Spacer height='md' />

      <ViewContractButton
        reviewed={reviewed}
        dateAccepted={orgSignUpState?.state?.dateAccepted}
        onViewAgreement={onViewAgreement}
      />
      <Spacer height='sm' />

      <ContractActionButtons
        agreeText={agreeText}
        resumeAddPackage={resumeAddPackage}
        reviewed={reviewed}
        navBack={navBack}
        onSubmit={onSubmit}
      />
      <Spacer height='sm' />
    </Column>
  );
};

export default Contract;
